/**
 * Focused hook for divisional chakra visibility management
 * Handles show/hide toggle state, chart settings read/write, and event emission
 */

import { devLog, devError } from '../utils/devLog';
import { useState, useCallback } from 'react';
import { useMultipleCharts } from '../components/common/AstrologyChart/MultipleChartsContext';
import { globalEventStream } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import {
  DivisionalChakraType,
  ChakraStartPoint,
  CustomStartPointSettings,
  getDivisionalChakraConfig
} from '../config/divisionalChakraConfig';

export interface ChakraVisibilityState {
  showChakra: boolean;
  setShowChakra: React.Dispatch<React.SetStateAction<boolean>>;
  getChakraSetting: () => boolean;
  updateVisibility: (
    enabled: boolean,
    currentStartPoint: ChakraStartPoint,
    currentCustomSettings: CustomStartPointSettings
  ) => Promise<void>;
}

/**
 * Hook for managing chakra visibility (show/hide) for the active chart
 */
export const useChakraVisibility = (
  chakraType: DivisionalChakraType,
  isLoading: boolean,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>
): ChakraVisibilityState => {
  const { activeChartId, getActiveChart, updateChart } = useMultipleCharts();
  const config = getDivisionalChakraConfig(chakraType);
  const settingKey = `show${chakraType.toUpperCase()}Chakra` as const;
  const startPointKey = `${chakraType}StartPoint` as const;

  const getChakraSetting = useCallback((): boolean => {
    try {
      if (!activeChartId) {
        return config.defaultVisible;
      }

      const activeChart = getActiveChart();
      if (!activeChart || !activeChart.settings) {
        return config.defaultVisible;
      }

      // Check both key formats: showNAKSHATRAChakra (hook format) and showNakshatraChakra (context format)
      const setting = (activeChart.settings as any)[settingKey];
      if (typeof setting === 'boolean') {
        return setting;
      }

      // Fallback: check camelCase format (e.g., showNakshatraChakra)
      const camelCaseKey = `show${chakraType.charAt(0).toUpperCase()}${chakraType.slice(1)}Chakra`;
      const camelCaseSetting = (activeChart.settings as any)[camelCaseKey];
      if (typeof camelCaseSetting === 'boolean') {
        return camelCaseSetting;
      }

      return config.defaultVisible;
    } catch (error) {
      devError(`useChakraVisibility[${chakraType}]: Error getting chakra setting:`, error);
      setError(`Failed to get ${chakraType} chakra setting: ${error}`);
      return config.defaultVisible;
    }
  }, [activeChartId, getActiveChart, settingKey, config.defaultVisible, chakraType, setError]);

  const [showChakra, setShowChakra] = useState(() => {
    if (activeChartId) {
      return getChakraSetting();
    }
    return config.defaultVisible;
  });

  const updateVisibility = useCallback(async (
    enabled: boolean,
    currentStartPoint: ChakraStartPoint,
    currentCustomSettings: CustomStartPointSettings
  ): Promise<void> => {
    if (typeof enabled !== 'boolean') {
      const errorMsg = `Invalid ${chakraType} chakra setting value: ${enabled}`;
      devError(`useChakraVisibility[${chakraType}]:`, errorMsg);
      setError(errorMsg);
      return;
    }

    if (!activeChartId) {
      const errorMsg = `No active chart ID available for ${chakraType} chakra update`;
      devError(`useChakraVisibility[${chakraType}]:`, errorMsg);
      setError(errorMsg);
      return;
    }

    if (!updateChart) {
      const errorMsg = 'updateChart function not available';
      devError(`useChakraVisibility[${chakraType}]:`, errorMsg);
      setError(errorMsg);
      return;
    }

    const currentSetting = getChakraSetting();
    if (currentSetting === enabled) {
      devLog(`useChakraVisibility[${chakraType}]: Setting unchanged (${enabled}), skipping`);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const activeChart = getActiveChart();
      if (!activeChart) {
        throw new Error('Active chart became unavailable during update');
      }

      await updateChart(activeChartId, {
        settings: {
          ...activeChart.settings,
          [settingKey]: enabled,
          // Also save startPoint so it persists across reloads
          [startPointKey]: currentStartPoint
        }
      });

      setShowChakra(enabled);

      // Emit event through globalEventStream
      globalEventStream.emit({
        id: `${chakraType}-toggle-${activeChartId}-${Date.now()}`,
        type: `${chakraType}.chakra.toggle`,
        source: `divisional-chakra-settings-hook-${chakraType}`,
        timestamp: Date.now(),
        chartId: activeChartId,
        data: {
          chartId: activeChartId,
          [`show${chakraType.toUpperCase()}Chakra`]: enabled,
          startPoint: currentStartPoint,
          customSettings: currentStartPoint === 'custom' ? currentCustomSettings : undefined
        }
      });
    } catch (error) {
      const errorMsg = `Failed to update ${chakraType} chakra setting: ${error}`;
      devError(`useChakraVisibility[${chakraType}]:`, errorMsg);
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [activeChartId, getActiveChart, updateChart, getChakraSetting, settingKey, startPointKey, chakraType, setIsLoading, setError]);

  return {
    showChakra,
    setShowChakra,
    getChakraSetting,
    updateVisibility
  };
};
